@if (isLoading) {
  <div class="profile-loading">
    Cargando información completa de tu expediente...
  </div>
} @else if (student) {
  <div class="profile-page">

    <!-- FEEDBACK TOAST / ALERTAS -->
    @if (feedbackMessage) {
      <div class="feedback-toast toast-success">
        <span>✓</span> {{ feedbackMessage }}
      </div>
    }
    @if (feedbackError) {
      <div class="feedback-toast toast-error">
        <span>✕</span> {{ feedbackError }}
      </div>
    }

    <!-- =========================================================
         1. HERO DE PERFIL (ALINEADO A ADMIN + FOLIO COPIABLE SIN ICONOS)
         ========================================================= -->
    <section class="student-hero">
      <div class="hero-top-row">
        <div class="hero-main">
          <div class="hero-eyebrow-container">
            <span class="hero-eyebrow">EXPEDIENTE DEL ESTUDIANTE</span>
            <span class="hero-type-pill">SERVICIO SOCIAL</span>

            @if (student.accountStatus === 'pending') {
              <span class="status-pill pill-pending">⏳ Pendiente de Aceptación</span>
            } @else if (student.accountStatus === 'rejected') {
              <span class="status-pill pill-rejected">✕ Rechazado</span>
            } @else if (student.accountStatus === 'inactive') {
              <span class="status-pill pill-rejected">● Inactivo</span>
            } @else {
              <span class="status-pill pill-active">● Activo</span>
            }
          </div>

          <div class="hero-title-row">
            <h1 class="hero-name">
              {{ student.fullName || student.mail || 'Estudiante' }}
            </h1>

            @if (student.folio) {
              <div
                class="hero-folio-badge"
                role="button"
                tabindex="0"
                (click)="copyFolio(student.folio)"
                (keydown.enter)="copyFolio(student.folio)"
                (keydown.space)="copyFolio(student.folio); $event.preventDefault()"
                [title]="folioCopied ? '¡Folio copiado!' : 'Haz clic para copiar el folio al portapapeles'"
              >
                <span class="folio-tag">FOLIO:</span>
                <span class="folio-code">{{ student.folio }}</span>
                @if (folioCopied) {
                  <span class="folio-copied-toast">¡Copiado!</span>
                }
              </div>
            }
          </div>

          <p class="hero-subtitle">
            {{ student.schoolMail || student.mail }}
            @if (student.career || student.school) {
              <span class="hero-meta-dot">•</span>
              <span>{{ student.career || 'Sin carrera' }} — {{ student.school || 'Sin escuela registrada' }}</span>
            }
            @if (student.matricula) {
              <span class="hero-meta-dot">•</span>
              <span>Matrícula: {{ student.matricula }}</span>
            }
          </p>
        </div>
      </div>

      <!-- FILA DE MÉTRICAS DEL EXPEDIENTE (MISMA CANTIDAD DE INFORMACIÓN QUE ADMIN) -->
      <div class="hero-stats-row">
        <div class="hero-stat-card">
          <span class="hero-stat-label">ESTATUS</span>
          <div class="hero-stat-value">
            @if (student.accountStatus === 'pending') {
              <strong style="color: #fde047;">⏳ Pendiente</strong>
            } @else if (student.accountStatus === 'rejected') {
              <strong style="color: #fca5a5;">✕ Rechazado</strong>
            } @else if (student.accountStatus === 'inactive') {
              <strong style="color: #fca5a5;">● Inactivo</strong>
            } @else {
              <strong style="color: #86efac;">● Activo</strong>
            }
          </div>
          <small class="hero-stat-sub">Estado actual de acceso</small>
        </div>

        <div class="hero-stat-card hero-stat-card-combined">
          <div class="combined-stat-col">
            <span class="hero-stat-label">EMISIÓN DE EXPEDIENTE</span>
            <strong class="hero-stat-value">
              {{ (student.fechaEmisionExpediente || student.created) ? ((student.fechaEmisionExpediente || student.created) | date:'dd/MM/yyyy HH:mm') : 'N/A' }}
            </strong>
            <small class="hero-stat-sub">Finalización de formulario</small>
          </div>

          <div class="combined-stat-divider"></div>

          <div class="combined-stat-col">
            <span class="hero-stat-label">ACEPTACIÓN / EXPEDICIÓN</span>
            <strong class="hero-stat-value" [style.color]="student.accountStatus === 'active' ? '#86efac' : 'rgba(255, 255, 255, 0.9)'">
              {{ (student.fechaAceptacionExpediente || student.fechaExpedicionExpediente) ? ((student.fechaAceptacionExpediente || student.fechaExpedicionExpediente) | date:'dd/MM/yyyy HH:mm') : (student.accountStatus === 'pending' ? 'Pendiente' : 'N/A') }}
            </strong>
            <small class="hero-stat-sub">Validación de ABAQ</small>
          </div>
        </div>

        <div class="hero-stat-card">
          <span class="hero-stat-label">OPORTUNIDADES GENERADAS</span>
          <strong class="hero-stat-value">
            {{ opportunitiesCount }}
            <span style="font-size: 0.85rem; font-weight: 600; opacity: 0.85; margin-left: 2px;">
              {{ opportunitiesCount === 1 ? 'oportunidad' : 'oportunidades' }}
            </span>
          </strong>
          <small class="hero-stat-sub">Vinculaciones registradas</small>
        </div>

        <div class="hero-stat-card">
          <span class="hero-stat-label">ACTIVIDADES</span>
          <strong class="hero-stat-value">
            {{ activitiesCount }}
            <span style="font-size: 0.85rem; font-weight: 600; opacity: 0.85; margin-left: 2px;">
              {{ activitiesCount === 1 ? 'actividad' : 'actividades' }}
            </span>
          </strong>
          <small class="hero-stat-sub">Participación en programas</small>
        </div>

        <div class="hero-stat-card hero-stat-card-total">
          <span class="hero-stat-label">SUMA TOTAL</span>
          <strong class="hero-stat-value">
            {{ totalStatsCount }}
            <span style="font-size: 0.85rem; font-weight: 600; opacity: 0.85; margin-left: 2px;">
              {{ totalStatsCount === 1 ? 'registro' : 'registros' }}
            </span>
          </strong>
          <small class="hero-stat-sub">Oportunidades + actividades</small>
        </div>
      </div>
    </section>

    <!-- BITÁCORA DE ESTATUS / BAJA -->
    @if (student.accountStatus === 'inactive') {
      <div class="inactive-reason-banner">
        <div>
          <span class="inactive-banner-title">Cuenta dada de baja (inactiva)</span>
          <span class="inactive-banner-reason">
            Causal: {{ getFriendlyReason(lastStatusChange?.reason) }}
          </span>
          @if (lastStatusChange?.comments) {
            <p class="inactive-banner-comments">{{ lastStatusChange.comments }}</p>
          }
          @if (lastStatusChange?.date) {
            <span class="status-history-date">{{ lastStatusChange.date | date:'dd/MM/yyyy HH:mm' }}</span>
          }
        </div>
      </div>
    } @else if (student.accountStatus === 'active') {
      <div class="status-active-card">
        <div class="status-active-header">
          <div class="status-active-badge">
            <span class="status-badge-dot"></span>
            <span>Cuenta Activa y Verificada</span>
          </div>
          <span class="status-active-date">
            {{ (lastStatusChange?.date || student.fechaAceptacionExpediente || student.fechaExpedicionExpediente) ? ((lastStatusChange?.date || student.fechaAceptacionExpediente || student.fechaExpedicionExpediente) | date:'dd/MM/yyyy HH:mm') : 'Activo' }}
          </span>
        </div>
        <div class="status-active-body">
          <p class="status-active-message">
            Tu expediente y registro han sido formalmente aprobados por la Coordinación de ABAQ. Tienes acceso completo a todas las funciones y acreditaciones del servicio social.
          </p>
          @if (lastStatusChange?.comments) {
            <div class="status-active-comments">
              <span class="material-symbols-outlined comment-icon">chat_bubble_outline</span>
              <div>
                <strong>Mensaje de Coordinación:</strong>
                <p>{{ lastStatusChange.comments }}</p>
              </div>
            </div>
          }
        </div>
      </div>
    }

    <!-- =========================================================
         2. RESUMEN Y CONTROL DE HORAS (SOLO LECTURA)
         ========================================================= -->
    <section class="profile-card">
      <div class="section-header-row">
        <div>
          <span class="section-eyebrow">SERVICIO SOCIAL</span>
          <h2>Resumen y control de horas</h2>
          <p>Seguimiento institucional de horas reglamentarias y acreditaciones validadas.</p>
        </div>
      </div>

      <div class="summary-cards-grid">
        <article class="summary-card">
          <span class="summary-card-label">Horas requeridas</span>
          <strong class="summary-card-value">{{ student.horasCubrir }} h</strong>
          <span class="summary-card-sub">Total del periodo actual</span>
        </article>

        <article class="summary-card highlight-card">
          <span class="summary-card-label">Horas acreditadas</span>
          <strong class="summary-card-value">{{ student.horasAcreditadas }} h</strong>
          <span class="summary-card-sub">Progreso validado</span>
        </article>

        <article class="summary-card warning-card">
          <span class="summary-card-label">Horas pendientes</span>
          <strong class="summary-card-value">{{ student.horasPendientes }} h</strong>
          <span class="summary-card-sub">Restantes por acreditar</span>
        </article>

        <article class="summary-card accent-card">
          <span class="summary-card-label">Progreso</span>
          <strong class="summary-card-value">
            {{ (student.horasCubrir > 0 ? (student.horasAcreditadas / student.horasCubrir) * 100 : 0) | number:'1.0-0' }}%
          </strong>
          <span class="summary-card-sub">Servicio social completado</span>
        </article>

        <article class="summary-card">
          <span class="summary-card-label">Actividades</span>
          <strong class="summary-card-value">{{ activitiesCount }}</strong>
          <span class="summary-card-sub">Registros totales</span>
        </article>
      </div>
    </section>

    <!-- =========================================================
         3. INFORMACIÓN PERSONAL Y ESCOLAR (EDITABLE: TELÉFONO Y CORREO PERSONAL)
         ========================================================= -->
    <section class="profile-card">
      <div class="section-header-row">
        <div>
          <span class="section-eyebrow">PERFIL</span>
          <h2>Información del estudiante</h2>
          <p>Datos registrados en tu expediente escolar y de contacto.</p>
        </div>

        <div class="edit-btn-group">
          @if (!isEditingPersonal) {
            <button
              type="button"
              class="btn-section-edit"
              (click)="startEditPersonal()"
              title="Editar datos de contacto"
            >
              Editar información
            </button>
          } @else {
            <button
              type="button"
              class="btn-section-cancel"
              (click)="cancelEditPersonal()"
              [disabled]="isSavingPersonal"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn-section-save"
              (click)="savePersonal()"
              [disabled]="isSavingPersonal"
            >
              @if (isSavingPersonal) { Guardando... } @else { ✓ Guardar cambios }
            </button>
          }
        </div>
      </div>

      <div class="info-grid">
        <article class="info-card">
          <span class="info-card-span">FOLIO DE EXPEDIENTE</span>
          <span class="info-card-value value-monospace">
            {{ student.folio || '—' }}
          </span>
        </article>

        <article class="info-card">
          <span class="info-card-span">FECHA DE EMISIÓN DE EXPEDIENTE</span>
          <span class="info-card-value">
            {{ student.fechaEmisionExpediente ? (student.fechaEmisionExpediente | date:'dd/MM/yyyy HH:mm') : 'N/A' }}
          </span>
        </article>

        <article class="info-card">
          <span class="info-card-span">FECHA DE ACEPTACIÓN / EXPEDICIÓN</span>
          <span class="info-card-value" [style.color]="student.accountStatus === 'active' ? '#059669' : '#1e293b'">
            {{ (student.fechaAceptacionExpediente || student.fechaExpedicionExpediente) ? ((student.fechaAceptacionExpediente || student.fechaExpedicionExpediente) | date:'dd/MM/yyyy HH:mm') : (student.accountStatus === 'pending' ? 'Pendiente de aceptación' : 'N/A') }}
          </span>
        </article>

        <article class="info-card">
          <span class="info-card-span">TIEMPO DE LLENADO DE FORMULARIO</span>
          <span class="info-card-value">
            {{ student.tiempoLlenadoMinutosExpediente !== undefined && student.tiempoLlenadoMinutosExpediente !== null ? student.tiempoLlenadoMinutosExpediente + ' min' : 'N/A' }}
          </span>
        </article>

        <article class="info-card">
          <span class="info-card-span">NOMBRE COMPLETO</span>
          <span class="info-card-value">
            {{ student.fullName || 'N/A' }}
          </span>
        </article>

        <article class="info-card">
          <span class="info-card-span">MATRÍCULA</span>
          <span class="info-card-value">
            {{ student.matricula || 'N/A' }}
          </span>
        </article>

        <article class="info-card">
          <span class="info-card-span">CURP</span>
          <span class="info-card-value value-monospace" style="text-transform: uppercase;">
            {{ student.curp || 'No registrada' }}
          </span>
        </article>

        <article class="info-card">
          <span class="info-card-span">CORREO INSTITUCIONAL</span>
          <span class="info-card-value">
            {{ student.schoolMail || 'No registrado' }}
          </span>
        </article>

        <article class="info-card">
          <span class="info-card-span">
            CORREO PERSONAL
            @if (isEditingPersonal) {
              <span class="badge-editable-indicator">Editable</span>
            }
          </span>
          @if (isEditingPersonal) {
            <input
              type="email"
              class="input-editable-active"
              [(ngModel)]="student.mail"
              placeholder="correo@ejemplo.com"
            />
          } @else {
            <span class="info-card-value">
              {{ student.mail || 'No registrado' }}
            </span>
          }
        </article>

        <article class="info-card">
          <span class="info-card-span">
            TELÉFONO
            @if (isEditingPersonal) {
              <span class="badge-editable-indicator">Editable</span>
            }
          </span>
          @if (isEditingPersonal) {
            <input
              type="tel"
              class="input-editable-active"
              [(ngModel)]="student.phone"
              placeholder="Ej. 4421234567"
            />
          } @else {
            <span class="info-card-value">
              {{ student.phone || 'No registrado' }}
            </span>
          }
        </article>

        <article class="info-card">
          <span class="info-card-span">¿TIENE WHATSAPP?</span>
          <span class="info-card-value">
            {{ student.hasWhatsapp ? '✓ Sí (' + (student.whatsappNumber || student.phone) + ')' : 'No registrado' }}
          </span>
        </article>

        <article class="info-card">
          <span class="info-card-span">CONTACTO SECUNDARIO / EMERGENCIA</span>
          <span class="info-card-value">
            {{ student.hasSecondaryContact && student.secondaryContactPhone ? student.secondaryContactPhone : 'No registrado' }}
          </span>
        </article>

        <article class="info-card">
          <span class="info-card-span">ESCUELA / UNIVERSIDAD</span>
          <span class="info-card-value">
            {{ student.school || 'N/A' }}
          </span>
        </article>

        <article class="info-card">
          <span class="info-card-span">CARRERA</span>
          <span class="info-card-value">
            {{ student.career || 'N/A' }}
          </span>
        </article>

        <article class="info-card">
          <span class="info-card-span">PERIODO / AVANCE ACADÉMICO</span>
          <span class="info-card-value">
            {{ student.semester || 'N/A' }}
          </span>
        </article>

        <article class="info-card">
          <span class="info-card-span">AUDITOR RESPONSABLE</span>
          <span class="info-card-value">
            {{ student.auditorName || 'N/A' }}
          </span>
        </article>

        <article class="info-card">
          <span class="info-card-span">FECHA DE NACIMIENTO</span>
          <span class="info-card-value">
            {{ student.birthDate ? (student.birthDate | date:'dd/MM/yyyy') : 'No registrada' }}
          </span>
        </article>

        <article class="info-card">
          <span class="info-card-span">FECHA DE INICIO DEL SERVICIO SOCIAL</span>
          <span class="info-card-value">
            {{ student.periods.length ? (student.periods[0].begin | date:'dd/MM/yyyy') : 'N/A' }}
          </span>
          <small class="info-card-hint">Inicio formal en ABAQ</small>
        </article>

        <article class="info-card">
          <span class="info-card-span">FECHA DE CONCLUSIÓN DEL SERVICIO SOCIAL</span>
          <span class="info-card-value">
            {{ student.periods.length ? (student.periods[0].end | date:'dd/MM/yyyy') : 'N/A' }}
          </span>
          <small class="info-card-hint">Límite para acreditar horas</small>
        </article>

        <article class="info-card">
          <span class="info-card-span">HORAS REQUERIDAS DEL PERIODO</span>
          <span class="info-card-value">
            {{ student.periods.length ? student.periods[0].desiredHours : student.horasCubrir }} h
          </span>
        </article>

        <article class="info-card">
          <span class="info-card-span">¿CÓMO SUPISTE DE ABAQ?</span>
          <span class="info-card-value">
            {{ student.comoSupisteDeAbaq || 'No especificado' }}
          </span>
        </article>
      </div>
    </section>

    <!-- =========================================================
         4. EXPEDIENTE DIGITAL EN LA NUBE (SOLO LECTURA - ENLACE PÚBLICO DIRECTO)
         ========================================================= -->
    <section class="profile-card">
      <div class="section-header-row">
        <div>
          <span class="section-eyebrow">EXPEDIENTE DIGITAL</span>
          <h2>Documentos del expediente en la nube</h2>
          <p>Enlaces públicos registrados en tu expediente institucional. Haz clic para abrir en una pestaña nueva.</p>
        </div>
      </div>

      <div class="doc-subgroup">
        <div class="doc-subgroup-header">
          <h3>Documentos de Registro e Identidad (Onboarding)</h3>
          <span>Enlaces compartidos durante el registro del expediente institucional</span>
        </div>

        <div class="cloud-docs-grid">
          <article class="cloud-doc-card">
            <span class="cloud-doc-label">IDENTIFICACIÓN OFICIAL (INE / ESCOLAR)</span>
            @if (hasValidDocument(student.cloudUrls?.ine)) {
              <span class="cloud-doc-status">✓ Enlace disponible</span>
              <button
                type="button"
                class="btn-open-doc"
                (click)="openDocument(student.cloudUrls?.ine)"
              >
                Abrir documento público ↗
              </button>
            } @else {
              <span class="doc-pending-text">No proporcionado</span>
            }
          </article>

          <article class="cloud-doc-card">
            <span class="cloud-doc-label">CURRÍCULUM VITAE (CV) / SEMBLANZA</span>
            @if (hasValidDocument(student.cloudUrls?.cv)) {
              <span class="cloud-doc-status">✓ Enlace disponible</span>
              <button
                type="button"
                class="btn-open-doc"
                (click)="openDocument(student.cloudUrls?.cv)"
              >
                Abrir documento público ↗
              </button>
            } @else {
              <span class="doc-pending-text">No proporcionado</span>
            }
          </article>

          <article class="cloud-doc-card">
            <span class="cloud-doc-label">COMPROBANTE DE DOMICILIO O ESTUDIOS</span>
            @if (hasValidDocument(student.cloudUrls?.comprobante)) {
              <span class="cloud-doc-status">✓ Enlace disponible</span>
              <button
                type="button"
                class="btn-open-doc"
                (click)="openDocument(student.cloudUrls?.comprobante)"
              >
                Abrir documento público ↗
              </button>
            } @else {
              <span class="doc-pending-text">No proporcionado</span>
            }
          </article>
        </div>
      </div>

      <div class="doc-subgroup" style="margin-top: 24px;">
        <div class="doc-subgroup-header">
          <h3>Documentación Institucional de Servicio Social</h3>
          <span>Cartas y constancias del ciclo reglamentario de servicio social</span>
        </div>

        <div class="cloud-docs-grid">
          <article class="cloud-doc-card">
            <span class="cloud-doc-label">CONSTANCIA DE PLÁTICA DE INDUCCIÓN</span>
            @if (hasValidDocument(student.constanciaPlatica)) {
              <span class="cloud-doc-status">✓ Enlace disponible</span>
              <button
                type="button"
                class="btn-open-doc"
                (click)="openDocument(student.constanciaPlatica)"
              >
                Abrir documento público ↗
              </button>
            } @else {
              <span class="doc-pending-text">No proporcionado</span>
            }
          </article>

          <article class="cloud-doc-card">
            <span class="cloud-doc-label">REPORTE DE ACTIVIDADES</span>
            @if (hasValidDocument(student.reporteActividades)) {
              <span class="cloud-doc-status">✓ Enlace disponible</span>
              <button
                type="button"
                class="btn-open-doc"
                (click)="openDocument(student.reporteActividades)"
              >
                Abrir documento público ↗
              </button>
            } @else {
              <span class="doc-pending-text">No proporcionado</span>
            }
          </article>

          <article class="cloud-doc-card">
            <span class="cloud-doc-label">CARTA DE TÉRMINO (LIBERACIÓN)</span>
            @if (hasValidDocument(student.cartaTermino)) {
              <span class="cloud-doc-status">✓ Enlace disponible</span>
              <button
                type="button"
                class="btn-open-doc"
                (click)="openDocument(student.cartaTermino)"
              >
                Abrir documento público ↗
              </button>
            } @else {
              <span class="doc-pending-text">No proporcionado</span>
            }
          </article>
        </div>
      </div>
    </section>

    <!-- =========================================================
         5. DOCUMENTOS Y CONSTANCIAS OFICIALES ABAQ (HMAC-SHA256 - SOLO LECTURA)
         ========================================================= -->
    @if (student.accountStatus === 'active' || hasDocument(student, 'cartaAceptacion') || hasDocument(student, 'cartaTermino')) {
      <section class="profile-card">
        <div class="section-header-row">
          <div>
            <span class="section-eyebrow">DOCUMENTO OFICIAL</span>
            <h2>Constancias institucionales emitidas</h2>
            <p>Documentos oficiales generados por ABAQ con sello criptográfico HMAC-SHA256.</p>
          </div>
        </div>

        <div class="official-certs-container">
          <div class="official-cert-card">
            <div class="cert-left">
              <div class="cert-icon">📄</div>
              <div class="cert-info">
                <div class="cert-title">Carta de Aceptación y Felicitación · Servicio Social</div>
                <div class="cert-desc">
                  Emitida para {{ student.fullName }} con folio {{ student.folio || 'N/A' }}.
                </div>
                <div class="cert-meta">
                  Sello criptográfico HMAC-SHA256 · Verificable en línea con QR institucional
                </div>
              </div>
            </div>

            <button
              type="button"
              class="btn-view-cert"
              (click)="openAcceptanceCertificate(student)"
            >
              Ver y Descargar Constancia ↗
            </button>
          </div>

          @if (hasDocument(student, 'cartaTermino') || (student.horasCubrir > 0 && student.horasPendientes === 0)) {
            <div class="official-cert-card cert-completion">
              <div class="cert-left">
                <div class="cert-icon">🎓</div>
                <div class="cert-info">
                  <div class="cert-title">Constancia Oficial de Término y Liberación</div>
                  <div class="cert-desc">
                    Acreditación de {{ student.horasCubrir || 480 }} horas reglamentarias completadas.
                  </div>
                  <div class="cert-meta">
                    Sello criptográfico HMAC-SHA256 · ABAQ Querétaro
                  </div>
                </div>
              </div>

              <button
                type="button"
                class="btn-view-cert"
                (click)="openCompletionCertificate(student)"
              >
                Ver Carta de Término ↗
              </button>
            </div>
          }
        </div>
      </section>
    }

    <!-- =========================================================
         6. SEMBLANZA, INTERESES, TALENTOS Y VOCACIÓN (EDITABLE: BIO PERSONAL)
         ========================================================= -->
    <section class="profile-card">
      <div class="section-header-row">
        <div>
          <span class="section-eyebrow">FASE 3 · SOBRE MÍ</span>
          <h2>Semblanza, Intereses, Talentos y Vocación</h2>
          <p>Perfil vocacional y afinidad al bienestar animal del estudiante.</p>
        </div>

        <div class="edit-btn-group">
          @if (!isEditingBio) {
            <button
              type="button"
              class="btn-section-edit"
              (click)="startEditBio()"
              title="Editar semblanza personal"
            >
              Editar información
            </button>
          } @else {
            <button
              type="button"
              class="btn-section-cancel"
              (click)="cancelEditBio()"
              [disabled]="isSavingBio"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn-section-save"
              (click)="saveBio()"
              [disabled]="isSavingBio"
            >
              @if (isSavingBio) { Guardando... } @else { ✓ Guardar cambios }
            </button>
          }
        </div>
      </div>

      <div class="bio-section-body">
        <div class="bio-card-container">
          <span class="bio-card-label">
            SEMBLANZA / BIO PERSONAL
            @if (isEditingBio) {
              <span class="badge-editable-indicator">Editable</span>
            }
          </span>
          @if (isEditingBio) {
            <textarea
              class="bio-textarea"
              rows="4"
              [(ngModel)]="student.bioPersonal"
              placeholder="Comparte tu semblanza personal, trayectoria y motivaciones en ABAQ..."
            ></textarea>
          } @else {
            <p class="bio-display-text">
              {{ student.bioPersonal || 'Sin semblanza registrada.' }}
            </p>
          }
        </div>

        <div class="vocational-grid">
          <div class="vocational-card">
            <span class="vocational-card-label">INTERESES Y PASIONES</span>
            <p class="vocational-text">
              {{ student.intereses || 'No especificados' }}
            </p>
          </div>

          <div class="vocational-card">
            <span class="vocational-card-label">TALENTOS Y HABILIDADES ESPECIALES</span>
            <p class="vocational-text">
              {{ student.talentos || 'No especificados' }}
            </p>
          </div>

          <div class="vocational-card">
            <span class="vocational-card-label">APORTES PRINCIPALES A ABAQ</span>
            <p class="vocational-text">
              {{ student.aportesAbaq || 'No especificados' }}
            </p>
          </div>

          <div class="vocational-card">
            <span class="vocational-card-label">EXPERIENCIA PREVIA CON ANIMALES</span>
            <p class="vocational-text">
              {{ student.animalExperience ? '✓ Sí: ' + (student.descExperiencia || 'Convivencia') : 'Sin experiencia previa registrada' }}
            </p>
          </div>

          <div class="vocational-card card-full-width">
            <span class="vocational-card-label">MOTIVACIÓN PARA UNIRSE A ABAQ</span>
            <p class="vocational-text">
              {{ student.motivacionAbaq || 'No especificada' }}
            </p>
          </div>
        </div>

        @if (student.rankedActivities && student.rankedActivities.length > 0) {
          <div class="vocational-card">
            <div class="ranked-header-row">
              <span class="vocational-card-label" style="margin-bottom: 0;">
                🎯 ACTIVIDADES DE SERVICIO SOCIAL PRIORIZADAS
              </span>
              <span class="ranked-count-badge">
                {{ student.rankedActivities.length }} actividades
              </span>
            </div>

            <div class="sd-combined-table-wrapper" style="margin-top: 12px;">
              <table class="sd-combined-table">
                <thead>
                  <tr>
                    <th style="width: 110px;"># Prioridad</th>
                    <th>Actividad reglamentaria priorizada</th>
                    <th style="width: 220px;">Acreditación estimada</th>
                  </tr>
                </thead>
                <tbody>
                  @for (act of student.rankedActivities; track $index) {
                    <tr>
                      <td>
                        <span class="priority-rank-pill">#{{ $index + 1 }}</span>
                      </td>
                      <td>
                        <strong>{{ getActivityTitle(act) }}</strong>
                      </td>
                      <td>
                        @if (getActivityHours(act)) {
                          <span class="performance-pill perf-neutral">
                            ⏱ {{ getActivityHours(act) }}
                          </span>
                        } @else {
                          —
                        }
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        }
      </div>
    </section>

    <!-- =========================================================
         7. INDUCCIÓN Y REFLEXIONES DE LOS 6 MÓDULOS FORMATIVOS (FASE 2 - SOLO LECTURA)
         ========================================================= -->
    <section class="profile-card">
      <div class="section-header-row">
        <div>
          <span class="section-eyebrow">FASE 2 · INDUCCIÓN</span>
          <h2>Reflexiones de los 6 Módulos Formativos</h2>
          <p>Respuestas registradas durante la inducción institucional sobre rescate, valores y misión de ABAQ.</p>
        </div>
      </div>

      @if (hasAnyReflections()) {
        <div class="reflections-accordion">
          @for (mod of reflectionsList; track mod.id) {
            <div class="module-reflection-card">
              <div class="reflection-header">
                {{ mod.name }}
              </div>

              @if (mod.data) {
                <div class="reflection-cards-grid">
                  <div class="reflection-answer-box">
                    <span class="reflection-q-label q-liked">❤️ Lo que más llamó mi atención:</span>
                    <p class="reflection-a-text">{{ mod.data.liked || 'Sin respuesta' }}</p>
                  </div>
                  <div class="reflection-answer-box">
                    <span class="reflection-q-label q-important">⭐ Lo que considero más importante:</span>
                    <p class="reflection-a-text">{{ mod.data.important || 'Sin respuesta' }}</p>
                  </div>
                  <div class="reflection-answer-box">
                    <span class="reflection-q-label q-improve">💡 Sugerencia de mejora / propuesta:</span>
                    <p class="reflection-a-text">{{ mod.data.improve || 'Sin respuesta' }}</p>
                  </div>
                </div>
              } @else {
                <span class="no-reflection-note">Módulo sin respuestas registradas.</span>
              }
            </div>
          }
        </div>
      } @else {
        <div class="empty-state">
          <strong>Sin reflexiones de inducción registradas.</strong>
          <span>Las respuestas formativas capturadas en la Fase 2 se muestran aquí.</span>
        </div>
      }
    </section>

    <!-- =========================================================
         8. NORMATIVA Y CUMPLIMIENTO LEGAL (FASE 4 - SOLO LECTURA)
         ========================================================= -->
    <section class="profile-card">
      <div class="section-header-row">
        <div>
          <span class="section-eyebrow">NORMATIVA Y CUMPLIMIENTO</span>
          <h2>Consentimientos y Términos Aceptados</h2>
          <p>Condiciones y normativas validadas durante el registro institucional. Haz clic para consultar cada documento.</p>
        </div>
      </div>

      <div class="consent-grid">
        <a
          [href]="getLegalDocUrl('reglamento')"
          (click)="openLegalDoc('reglamento', $event)"
          target="_blank"
          rel="noopener noreferrer"
          class="consent-doc-link"
          title="Abrir Reglamento General Interno en otra pestaña"
        >
          <div class="consent-left">
            <span class="consent-status-icon">{{ student.consents?.reglamento ? '✅' : '❌' }}</span>
            <span class="consent-title">Reglamento General Interno</span>
          </div>
          <span class="consent-ext-icon">↗</span>
        </a>

        <a
          [href]="getLegalDocUrl('privacidad')"
          (click)="openLegalDoc('privacidad', $event)"
          target="_blank"
          rel="noopener noreferrer"
          class="consent-doc-link"
          title="Abrir Aviso de Privacidad Integral en otra pestaña"
        >
          <div class="consent-left">
            <span class="consent-status-icon">{{ student.consents?.privacidad ? '✅' : '❌' }}</span>
            <span class="consent-title">Aviso de Privacidad Integral</span>
          </div>
          <span class="consent-ext-icon">↗</span>
        </a>

        <a
          [href]="getLegalDocUrl('etica')"
          (click)="openLegalDoc('etica', $event)"
          target="_blank"
          rel="noopener noreferrer"
          class="consent-doc-link"
          title="Abrir Código de Ética y Conducta en otra pestaña"
        >
          <div class="consent-left">
            <span class="consent-status-icon">{{ student.consents?.etica ? '✅' : '❌' }}</span>
            <span class="consent-title">Código de Ética y Conducta</span>
          </div>
          <span class="consent-ext-icon">↗</span>
        </a>

        <a
          [href]="getLegalDocUrl('incognito')"
          (click)="openLegalDoc('incognito', $event)"
          target="_blank"
          rel="noopener noreferrer"
          class="consent-doc-link"
          title="Abrir Permiso de Difusión Institucional en otra pestaña"
        >
          <div class="consent-left">
            <span class="consent-status-icon">{{ student.consents?.incognito ? '✅' : '❌' }}</span>
            <span class="consent-title">Permiso de Difusión Institucional</span>
          </div>
          <span class="consent-ext-icon">↗</span>
        </a>

        <a
          [href]="getLegalDocUrl('consentimiento')"
          (click)="openLegalDoc('consentimiento', $event)"
          target="_blank"
          rel="noopener noreferrer"
          class="consent-doc-link"
          title="Abrir Consentimiento Voluntario Expreso en otra pestaña"
        >
          <div class="consent-left">
            <span class="consent-status-icon">{{ student.consents?.consentimiento ? '✅' : '❌' }}</span>
            <span class="consent-title">Consentimiento Voluntario Expreso</span>
          </div>
          <span class="consent-ext-icon">↗</span>
        </a>
      </div>
    </section>

    <!-- =========================================================
         9. COMUNIDAD Y REDES SOCIALES (EDITABLE)
         ========================================================= -->
    <section class="profile-card">
      <div class="section-header-row">
        <div>
          <span class="section-eyebrow">COMUNIDAD</span>
          <h2>Redes sociales y canales de difusión</h2>
          <p>Cuentas personales vinculadas para comunicación institucional y difusión animal.</p>
        </div>

        <div class="edit-btn-group">
          @if (!isEditingSocials) {
            <button
              type="button"
              class="btn-section-edit"
              (click)="startEditSocials()"
              title="Editar cuentas de redes sociales"
            >
              Editar información
            </button>
          } @else {
            <button
              type="button"
              class="btn-section-cancel"
              (click)="cancelEditSocials()"
              [disabled]="isSavingSocials"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn-section-save"
              (click)="saveSocials()"
              [disabled]="isSavingSocials"
            >
              @if (isSavingSocials) { Guardando... } @else { ✓ Guardar cambios }
            </button>
          }
        </div>
      </div>

      <div class="social-cards-grid">
        <article class="social-network-card">
          <div class="social-logo fb" title="Facebook">
            <svg class="lucide lucide-facebook" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
            </svg>
          </div>
          <div class="social-info">
            <span class="social-name">FACEBOOK</span>
            @if (isEditingSocials) {
              <input
                type="text"
                class="social-input"
                [(ngModel)]="student.socialMedia.facebook"
                placeholder="Enlace o usuario"
              />
            } @else {
              <span class="social-static-value">
                {{ student.socialMedia.facebook || 'Sin cuenta' }}
              </span>
            }
          </div>
        </article>

        <article class="social-network-card">
          <div class="social-logo ig" title="Instagram">
            <svg class="lucide lucide-instagram" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect width="20" height="20" x="2" y="2" rx="5" ry="5"/>
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
              <line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/>
            </svg>
          </div>
          <div class="social-info">
            <span class="social-name">INSTAGRAM</span>
            @if (isEditingSocials) {
              <input
                type="text"
                class="social-input"
                [(ngModel)]="student.socialMedia.instagram"
                placeholder="@usuario"
              />
            } @else {
              <span class="social-static-value">
                {{ student.socialMedia.instagram || 'Sin cuenta' }}
              </span>
            }
          </div>
        </article>

        <article class="social-network-card">
          <div class="social-logo tk" title="TikTok">
            <svg class="lucide lucide-tiktok" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"/>
            </svg>
          </div>
          <div class="social-info">
            <span class="social-name">TIKTOK</span>
            @if (isEditingSocials) {
              <input
                type="text"
                class="social-input"
                [(ngModel)]="student.socialMedia.tiktok"
                placeholder="@usuario"
              />
            } @else {
              <span class="social-static-value">
                {{ student.socialMedia.tiktok || 'Sin cuenta' }}
              </span>
            }
          </div>
        </article>

        <article class="social-network-card">
          <div class="social-logo x" title="X (Twitter)">
            <svg class="lucide lucide-twitter" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 4l11.733 16h4.267l-11.733 -16z"/>
              <path d="M4 20l6.768 -6.768m2.46 -2.46l6.772 -6.772"/>
            </svg>
          </div>
          <div class="social-info">
            <span class="social-name">X (TWITTER)</span>
            @if (isEditingSocials) {
              <input
                type="text"
                class="social-input"
                [(ngModel)]="student.socialMedia.x"
                placeholder="@usuario"
              />
            } @else {
              <span class="social-static-value">
                {{ student.socialMedia.x || 'Sin cuenta' }}
              </span>
            }
          </div>
        </article>

        <article class="social-network-card">
          <div class="social-logo yt" title="YouTube">
            <svg class="lucide lucide-youtube" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17"/>
              <polygon points="10 15 15 12 10 9 10 15"/>
            </svg>
          </div>
          <div class="social-info">
            <span class="social-name">YOUTUBE</span>
            @if (isEditingSocials) {
              <input
                type="text"
                class="social-input"
                [(ngModel)]="student.socialMedia.youtube"
                placeholder="Canal o enlace"
              />
            } @else {
              <span class="social-static-value">
                {{ student.socialMedia.youtube || 'Sin canal' }}
              </span>
            }
          </div>
        </article>

        <article class="social-network-card">
          <div class="social-logo li" title="LinkedIn">
            <svg class="lucide lucide-linkedin" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
              <rect width="4" height="12" x="2" y="9"/>
              <circle cx="4" cy="4" r="2"/>
            </svg>
          </div>
          <div class="social-info">
            <span class="social-name">LINKEDIN</span>
            @if (isEditingSocials) {
              <input
                type="text"
                class="social-input"
                [(ngModel)]="student.socialMedia.linkedin"
                placeholder="Perfil profesional"
              />
            } @else {
              <span class="social-static-value">
                {{ student.socialMedia.linkedin || 'Sin perfil' }}
              </span>
            }
          </div>
        </article>
      </div>
    </section>

    <!-- =========================================================
         10. ACREDITACIONES Y ACTIVIDADES ASIGNADAS (3 PESTAÑAS Y 3 TABLAS EN UN CONTENEDOR - SOLO LECTURA)
         ========================================================= -->
    <section class="profile-card">
      <div class="section-header-row">
        <div>
          <span class="section-eyebrow">PARTICIPACIÓN Y SERVICIO SOCIAL</span>
          <h2>Acreditaciones y actividades asignadas</h2>
          <p>Historial consolidado de acreditaciones otorgadas, actividades registradas, horas y calificación de desempeño.</p>
        </div>
      </div>

      <!-- PESTAÑAS: ACREDITACIONES / ACTIVIDADES / TODAS -->
      <nav class="sd-tabs" role="tablist" aria-label="Filtro de acreditaciones y actividades">
        <button
          type="button"
          role="tab"
          class="sd-tab"
          [class.active]="accActiveTab === 'acreditaciones'"
          [attr.aria-selected]="accActiveTab === 'acreditaciones'"
          (click)="setAccTab('acreditaciones')"
        >
          Acreditaciones
          <span class="sd-tab-count">{{ opportunitiesCount }}</span>
        </button>

        <button
          type="button"
          role="tab"
          class="sd-tab"
          [class.active]="accActiveTab === 'actividades'"
          [attr.aria-selected]="accActiveTab === 'actividades'"
          (click)="setAccTab('actividades')"
        >
          Actividades
          <span class="sd-tab-count">{{ activitiesCount }}</span>
        </button>

        <button
          type="button"
          role="tab"
          class="sd-tab"
          [class.active]="accActiveTab === 'todas'"
          [attr.aria-selected]="accActiveTab === 'todas'"
          (click)="setAccTab('todas')"
        >
          Todas
          <span class="sd-tab-count">{{ totalStatsCount }}</span>
        </button>
      </nav>

      <!-- TAB 1: TABLA DE ACREDITACIONES -->
      @if (accActiveTab === 'acreditaciones') {
        @if (accreditations.length > 0) {
          <div class="sd-combined-table-wrapper">
            <table class="sd-combined-table">
              <thead>
                <tr>
                  <th>Folio</th>
                  <th>Tipo de Acreditación</th>
                  <th>Horas</th>
                  <th>Calificación de Desempeño</th>
                  <th>Estado</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                @for (a of accreditations; track (a._id || a.interactionFolio || 'acc') + '-' + $index) {
                  <tr>
                    <td>
                      @if (a.interactionFolio) {
                        <app-folio-pill
                          [folio]="a.interactionFolio"
                          [interactionType]="a.interactionType || 'AC'"
                          [showHours]="false"
                        ></app-folio-pill>
                      } @else {
                        —
                      }
                    </td>
                    <td>
                      <strong>{{ getAccreditationTypeLabel(a.type) }}</strong>
                    </td>
                    <td>
                      <strong>{{ a.hoursAccredited ?? a.hoursPerformed ?? 0 }} h</strong>
                    </td>
                    <td>
                      <span class="performance-pill" [ngClass]="getPerformanceClass(a.status, a.performance)">
                        {{ getPerformanceLabel(a.status, a.performance) }}
                      </span>
                      @if (a.performanceComment || a.comments) {
                        <small class="table-comment-sub">{{ a.performanceComment || a.comments }}</small>
                      }
                    </td>
                    <td>
                      <span
                        class="performance-pill"
                        [ngClass]="a.status === 'approved' || a.status === 'accredited' ? 'perf-good' : (a.status === 'rejected' ? 'perf-bad' : 'perf-neutral')"
                      >
                        {{ getAccreditationStatusLabel(a.status) }}
                      </span>
                    </td>
                    <td>{{ a.date ? (a.date | date:'dd/MM/yyyy') : '—' }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        } @else {
          <div class="empty-state">
            <strong>Aún no tienes acreditaciones registradas.</strong>
            <span>Cuando los coordinadores validen tus oportunidades aparecerán aquí.</span>
          </div>
        }
      }

      <!-- TAB 2: TABLA DE ACTIVIDADES DEL SISTEMA -->
      @if (accActiveTab === 'actividades') {
        @if (student.assignedActivities && student.assignedActivities.length > 0) {
          <div class="sd-combined-table-wrapper">
            <table class="sd-combined-table">
              <thead>
                <tr>
                  <th>Folio</th>
                  <th>Actividad / Categoría</th>
                  <th>Horas</th>
                  <th>Calificación de Desempeño</th>
                  <th>Estado</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                @for (act of student.assignedActivities; track (act.activityRef || act.interactionFolio || 'act') + '-' + $index) {
                  <tr>
                    <td>
                      @if (act.interactionFolio) {
                        <app-folio-pill
                          [folio]="act.interactionFolio"
                          [interactionType]="act.interactionType || 'OP'"
                          [showHours]="false"
                        ></app-folio-pill>
                      } @else {
                        —
                      }
                    </td>
                    <td>
                      <strong>{{ act.title || act.type || 'Actividad institucional' }}</strong>
                      @if (act.title && act.type) {
                        <small class="table-comment-sub">{{ act.type }}</small>
                      }
                    </td>
                    <td>
                      <strong>{{ act.validatedHours ?? act.valueInHours ?? 0 }} h</strong>
                    </td>
                    <td>
                      <span class="performance-pill" [ngClass]="getPerformanceClass(act.status, act.performance)">
                        {{ getPerformanceLabel(act.status, act.performance) }}
                      </span>
                      @if (act.performanceComment) {
                        <small class="table-comment-sub">{{ act.performanceComment }}</small>
                      }
                    </td>
                    <td>
                      <span
                        class="performance-pill"
                        [ngClass]="act.status === 'validated' ? 'perf-good' : (act.status === 'absent' ? 'perf-absent' : 'perf-neutral')"
                      >
                        {{ getActivityStatusLabel(act.status) }}
                      </span>
                    </td>
                    <td>{{ (act.dueDate || act.assignedAt) ? ((act.dueDate || act.assignedAt) | date:'dd/MM/yyyy') : '—' }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        } @else {
          <div class="empty-state">
            <strong>No tienes actividades asignadas actualmente.</strong>
            <span>Cuando te registres en una actividad del sistema se mostrará aquí.</span>
          </div>
        }
      }

      <!-- TAB 3: TABLA COMBINADA DE TODAS (ACREDITACIONES + ACTIVIDADES) -->
      @if (accActiveTab === 'todas') {
        @if (totalStatsCount > 0) {
          <div class="sd-combined-table-wrapper">
            <table class="sd-combined-table">
              <thead>
                <tr>
                  <th>Folio</th>
                  <th>Origen</th>
                  <th>Tipo / Título</th>
                  <th>Horas</th>
                  <th>Calificación de Desempeño</th>
                  <th>Estado</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                @for (a of accreditations; track (a._id || a.interactionFolio || 'acc') + '-' + $index) {
                  <tr>
                    <td>
                      @if (a.interactionFolio) {
                        <app-folio-pill
                          [folio]="a.interactionFolio"
                          [interactionType]="a.interactionType || 'AC'"
                          [showHours]="false"
                        ></app-folio-pill>
                      } @else {
                        —
                      }
                    </td>
                    <td><span class="origin-badge origin-ac">Acreditación</span></td>
                    <td><strong>{{ getAccreditationTypeLabel(a.type) }}</strong></td>
                    <td><strong>{{ a.hoursAccredited ?? a.hoursPerformed ?? 0 }} h</strong></td>
                    <td>
                      <span class="performance-pill" [ngClass]="getPerformanceClass(a.status, a.performance)">
                        {{ getPerformanceLabel(a.status, a.performance) }}
                      </span>
                      @if (a.performanceComment || a.comments) {
                        <small class="table-comment-sub">{{ a.performanceComment || a.comments }}</small>
                      }
                    </td>
                    <td>
                      <span
                        class="performance-pill"
                        [ngClass]="a.status === 'approved' || a.status === 'accredited' ? 'perf-good' : (a.status === 'rejected' ? 'perf-bad' : 'perf-neutral')"
                      >
                        {{ getAccreditationStatusLabel(a.status) }}
                      </span>
                    </td>
                    <td>{{ a.date ? (a.date | date:'dd/MM/yyyy') : '—' }}</td>
                  </tr>
                }

                @for (ac of student.assignedActivities; track (ac.activityRef || ac.interactionFolio || 'act') + '-' + $index) {
                  <tr>
                    <td>
                      @if (ac.interactionFolio) {
                        <app-folio-pill
                          [folio]="ac.interactionFolio"
                          [interactionType]="ac.interactionType || 'OP'"
                          [showHours]="false"
                        ></app-folio-pill>
                      } @else {
                        —
                      }
                    </td>
                    <td><span class="origin-badge origin-act">Actividad</span></td>
                    <td><strong>{{ ac.title || ac.type || 'Actividad institucional' }}</strong></td>
                    <td><strong>{{ ac.validatedHours ?? ac.valueInHours ?? 0 }} h</strong></td>
                    <td>
                      <span class="performance-pill" [ngClass]="getPerformanceClass(ac.status, ac.performance)">
                        {{ getPerformanceLabel(ac.status, ac.performance) }}
                      </span>
                      @if (ac.performanceComment) {
                        <small class="table-comment-sub">{{ ac.performanceComment }}</small>
                      }
                    </td>
                    <td>
                      <span
                        class="performance-pill"
                        [ngClass]="ac.status === 'validated' ? 'perf-good' : (ac.status === 'absent' ? 'perf-absent' : 'perf-neutral')"
                      >
                        {{ getActivityStatusLabel(ac.status) }}
                      </span>
                    </td>
                    <td>{{ (ac.dueDate || ac.assignedAt) ? ((ac.dueDate || ac.assignedAt) | date:'dd/MM/yyyy') : '—' }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        } @else {
          <div class="empty-state">
            <strong>No hay registros de acreditaciones ni actividades disponibles.</strong>
          </div>
        }
      }
    </section>

    <!-- =========================================================
         11. SECCIÓN ESPECIAL DE PERFIL: MIS FOLIOS DE SERVICIO SOCIAL (SOLO LECTURA)
         ========================================================= -->
    @if (hasAnyFolios) {
      <section class="profile-card folios-participation-card">
        <div class="section-header-row">
          <div>
            <span class="section-eyebrow">TRAZABILIDAD INSTITUCIONAL</span>
            <h2>Mis folios de servicio social</h2>
            <p>Folios institucionales únicos asignados a tu expediente, actividades y acreditaciones.</p>
          </div>
        </div>

        <div class="folios-grid">
          @if (student.folio) {
            <div class="folio-item-box">
              <div>
                <small class="folio-item-kicker">EXPEDIENTE DE SERVICIO SOCIAL</small>
                <strong class="folio-item-title">Registro institucional</strong>
              </div>
              <app-folio-pill
                [folio]="student.folio"
                interactionType="AC"
                [showHours]="false"
              ></app-folio-pill>
            </div>
          }

          @for (activity of student.assignedActivities; track (activity.activityRef || activity.interactionFolio || 'f-act') + '-' + $index) {
            @if (activity.interactionFolio) {
              <div class="folio-item-box">
                <div>
                  <small class="folio-item-kicker">ACTIVIDAD ASIGNADA</small>
                  <strong class="folio-item-title">{{ activity.title || activity.type || 'Actividad ABAQ' }}</strong>
                </div>
                <app-folio-pill
                  [folio]="activity.interactionFolio"
                  [interactionType]="activity.interactionType || 'OP'"
                  [showHours]="true"
                  [hours]="activity.validatedHours ?? activity.valueInHours ?? 0"
                ></app-folio-pill>
              </div>
            }
          }

          @for (accreditation of accreditations; track (accreditation._id || accreditation.interactionFolio || 'f-acc') + '-' + $index) {
            @if (accreditation.interactionFolio) {
              <div class="folio-item-box">
                <div>
                  <small class="folio-item-kicker">ACREDITACIÓN DE COORDINADOR</small>
                  <strong class="folio-item-title">{{ getAccreditationTypeLabel(accreditation.type) }}</strong>
                </div>
                <app-folio-pill
                  [folio]="accreditation.interactionFolio"
                  [interactionType]="accreditation.interactionType || 'AC'"
                  [showHours]="true"
                  [hours]="accreditation.hoursAccredited ?? accreditation.hoursPerformed ?? 0"
                ></app-folio-pill>
              </div>
            }
          }
        </div>
      </section>
    }

  </div>
} @else {
  <div class="profile-error">
    No fue posible cargar la información de tu perfil escolar. Por favor intenta más tarde.
  </div>
}
